import { useState } from 'react';
import type { DailyUptime, Incident } from '../lib/types';
import { DayDetail } from './DayDetail';

interface Props {
  days: DailyUptime[];
  componentName: string;
  componentId: string;
  incidents: Incident[];
}

function colorFor(uptime: number | null): string {
  // bg-[var(--color-bg-subtle)] = grey "no data" bar (e.g. pre-launch days
  // still inside the 90d window, or snapshot-Lambda gaps).
  if (uptime === null) return 'bg-[var(--color-bg-subtle)]';
  if (uptime >= 0.999) return 'bg-status-operational';
  if (uptime >= 0.99) return 'bg-emerald-500';
  if (uptime >= 0.95) return 'bg-status-degraded';
  if (uptime >= 0.5) return 'bg-status-partial';
  return 'bg-status-major';
}

function labelFor(d: DailyUptime): string {
  return d.uptime === null
    ? `${d.date}: no data`
    : `${d.date}: ${(d.uptime * 100).toFixed(2)}% uptime`;
}

// "2026-05-26" -> "May 26". Used in the coverage-start footnote so we don't
// leak ISO strings into the UI, while keeping the format unambiguous.
function shortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function UptimeBars({ days, componentName, componentId, incidents }: Readonly<Props>) {
  const [hover, setHover] = useState<DailyUptime | null>(null);
  const [selected, setSelected] = useState<DailyUptime | null>(null);

  // Roll up only days with actual data so pre-launch grey bars don't drag the
  // headline number toward 0 (or, worse, falsely toward 100% as they did when
  // they were treated as up).
  const withData = days.filter((d): d is DailyUptime & { uptime: number } => d.uptime !== null);
  const overall =
    withData.length > 0
      ? withData.reduce((acc, d) => acc + d.uptime, 0) / withData.length
      : null;

  function toggle(d: DailyUptime) {
    setSelected((cur) => (cur?.date === d.date ? null : d));
  }

  // Hover wins over the rolling %. Either way, fall through to "no data"
  // when there's nothing meaningful to show. Pulled out of the JSX so we
  // don't have to nest the three states as a ternary (Sonar S3358).
  function summary(): string {
    if (hover) return labelFor(hover);
    if (overall === null) return 'no data';
    return `${(overall * 100).toFixed(2)}% uptime`;
  }

  // If the earliest data point is past day 0, the leading bars are "no
  // coverage" rather than "outage". Render those at half height so they
  // read as out-of-scope, and swap the leftmost label for the date coverage
  // actually began. Avoids the impression that 87 days of outage just happened.
  const firstWithData = days.find((d) => d.uptime !== null);
  const hasLeadingGap = firstWithData !== undefined && firstWithData !== days[0];
  const leftLabel = hasLeadingGap
    ? `Coverage began ${shortDate(firstWithData.date)}`
    : `${days.length} days ago`;

  return (
    <div className="mt-3" aria-label={`${componentName} 90-day uptime history`}>
      <div className="flex h-9 items-end gap-[2px]" role="img">
        {days.map((d) => {
          const isSelected = selected?.date === d.date;
          const heightCls = d.uptime === null ? 'h-1/2' : 'h-full';
          return (
            <button
              type="button"
              key={d.date}
              onClick={() => toggle(d)}
              className={`flex-1 rounded-[2px] transition-opacity hover:opacity-60 ${heightCls} ${colorFor(d.uptime)} ${
                isSelected ? 'ring-2 ring-[var(--color-fg)] ring-offset-1 ring-offset-[var(--color-bg-elevated)]' : ''
              }`}
              onMouseEnter={() => setHover(d)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(d)}
              onBlur={() => setHover(null)}
              aria-label={labelFor(d)}
              aria-pressed={isSelected}
            />
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between font-mono text-xs text-[var(--color-fg-muted)]">
        <span>{leftLabel}</span>
        <span aria-live="polite">{summary()}</span>
        <span>Today</span>
      </div>
      {selected && (
        <DayDetail
          day={selected}
          componentId={componentId}
          incidents={incidents}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
